import { useMonthSelector } from "$src/stores/use-month-selector";
import { useState } from "react";
import {
  useFloating,
  useClick,
  useDismiss,
  useInteractions,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
} from "@floating-ui/react";
import { ChevronDown } from "lucide-react";
import { cx } from "$src/lib/utils";
import { useNavigate, useLocation } from "react-router-dom";

import styles from "./month-selector.module.css";

const MONTH_OPTIONS = [
  { label: "200 sample", value: 1 },
  { label: "400 sample", value: 2 },
  { label: "600 sample", value: 3 },
  { label: "1200+ sample", value: 6 },
  { label: "200 Sample Vision", value: 10 },
];

export const MonthSelector = () => {
  const { selectedMonth, setSelectedMonth } = useMonthSelector();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const teamSlug = pathname.split("/")[1] || "early-moments";

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "bottom-start",
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const currentLabel =
    MONTH_OPTIONS.find((opt) => opt.value === selectedMonth)?.label || "200 sample";

  const handleSelect = (month: number) => {
    setSelectedMonth(month);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={refs.setReference}
        className={cx(styles.trigger, open && styles.triggerOpen)}
        {...getReferenceProps()}
      >
        <span className={styles.label}>{currentLabel}</span>
        <ChevronDown size={14} className={cx(styles.chevron, open && styles.chevronOpen)} />
      </button>

      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={styles.dropdown}
            {...getFloatingProps()}
          >
            {MONTH_OPTIONS.filter((o) => o.value !== 10).map((option) => (
              <button
                key={option.value}
                className={cx(
                  styles.option,
                  selectedMonth === option.value && styles.optionActive
                )}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </button>
            ))}
            <div className={styles.divider} />
            <button
              className={cx(
                styles.option,
                selectedMonth === 10 && styles.optionActive
              )}
              onClick={() => handleSelect(10)}
            >
              200 Sample Vision
            </button>
            <button
              className={styles.option}
              onClick={() => {
                navigate(`/${teamSlug}/vision`);
                setOpen(false);
              }}
            >
              Mid-Long term vision
            </button>
            <button
              className={styles.option}
              onClick={() => {
                navigate(`/${teamSlug}/data-exploration`);
                setOpen(false);
              }}
            >
              Data Exploration
            </button>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
